import * as React from "react";
import { LayoutGrid, List, type LucideIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { IconButton } from "./icon-button";

export type ViewMode = "board" | "list";

export interface ViewOption<T extends string = string> {
  value: T;
  label: string;
  icon: LucideIcon;
}

const DEFAULT_OPTIONS: ViewOption<ViewMode>[] = [
  { value: "board", label: "Board view", icon: LayoutGrid },
  { value: "list", label: "List view", icon: List },
];

export interface ToolbarViewToggleProps<T extends string = ViewMode> {
  view: T;
  onViewChange: (view: T) => void;
  options?: ViewOption<T>[];
  className?: string;
}

/**
 * Segmented icon toggle built from `IconButton` (spec §5.21). The selected
 * option reads as `bg-selected text-text` — no brand fill, no shadow.
 */
function ToolbarViewToggleInner<T extends string = ViewMode>(
  { view, onViewChange, options, className }: ToolbarViewToggleProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const items = (options ?? DEFAULT_OPTIONS) as ViewOption<T>[];

  return (
    <div
      ref={ref}
      role="tablist"
      aria-label="View options"
      className={cn(
        "inline-flex h-control shrink-0 items-center gap-0.5 rounded-md bg-hover p-0.5",
        className,
      )}
    >
      {items.map((opt) => {
        const Icon = opt.icon;
        return (
          <IconButton
            key={opt.value}
            role="tab"
            aria-selected={view === opt.value}
            size="sm"
            active={view === opt.value}
            label={opt.label}
            onClick={() => onViewChange(opt.value)}
          >
            <Icon aria-hidden="true" />
          </IconButton>
        );
      })}
    </div>
  );
}

const ToolbarViewToggle = React.forwardRef(ToolbarViewToggleInner) as <
  T extends string = ViewMode,
>(
  props: ToolbarViewToggleProps<T> & { ref?: React.Ref<HTMLDivElement> },
) => React.ReactElement | null;

export { ToolbarViewToggle };
